import { useEffect, useId, useRef, useState } from 'react'
import { dndSuUrl } from '../dnd-su.js'
import { Description } from './description.js'
import { useT } from './i18n-context.js'

/**
 * Термин со свёрнутым описанием (D57).
 *
 * Тестер не понял, что такое «Разносторонность»: на экране стоял
 * заголовок, под ним семнадцать названий черт, и ни одного слова
 * о том, что это значит. Описание при этом есть — в записи контента,
 * переведённое (D32), — просто на этот экран оно не выходило.
 *
 * Поэтому здесь не всплывающая подсказка браузера (`title`): на
 * телефоне её нет совсем, а за столом с телефона играет половина.
 * Знак вопроса — обычная кнопка с тач-целью 44 пикселя (D10),
 * и открывается она нажатием.
 *
 * Описание встаёт **в поток** и раздвигает содержимое, как это уже
 * сделано у заголовков разделов (`hint.tsx`). Всплывающее окно
 * пришлось бы позиционировать и следить, чтобы оно не накрыло сам
 * термин, который объясняет; у развёрнутого блока такой заботы нет
 * ни на какой ширине.
 *
 * Наведение открывает то же самое — но только там, где есть мышь:
 * на тач-экране `pointerenter` приходит вместе с нажатием, и подсказка
 * открывалась бы дважды. Открытое нажатием не закрывается уходом
 * курсора: иначе текст нельзя было бы выделить и скопировать.
 *
 * И только если курсор остановился: без задержки подсказки открывались
 * у каждой кнопки, через которую курсор просто проходит. На экране
 * создания их восемьдесят одна, страница от этого дёргалась, а до той
 * кнопки, к которой человек вёл курсор, доехать было нельзя — она
 * уезжала от раскрытых по дороге. Поймано живой проверкой.
 *
 * Разметка — одни `span`: компонент встаёт и внутрь `legend`,
 * где блочные элементы не разрешены. Вид блока даёт CSS.
 */
/**
 * Сколько курсор должен постоять, чтобы подсказка открылась.
 *
 * Меньше — и она открывается у кнопок, через которые курсор проходит
 * по дороге; больше — и наведение перестаёт работать, человек успевает
 * нажать.
 */
const HOVER_DELAY_MS = 400

export function TermHint({
  /** Название термина: уходит в подпись кнопки для голосового доступа. */
  name,
  /** Описание из записи, по-английски; `null` — описания нет. */
  text,
  /** Наш перевод описания (D32); `null` — ещё не переведено. */
  textRu,
  /**
   * Ключ записи для ссылки на dnd.su. Ссылки может не быть —
   * тогда подсказка показывает только наш текст.
   */
  contentId = null,
}: {
  readonly name: string
  readonly text: string | null
  readonly textRu: string | null
  readonly contentId?: string | null
}) {
  const t = useT()
  const [open, setOpen] = useState(false)
  // Открытое наведением закрывается уходом курсора, открытое
  // нажатием — только нажатием: это два разных обещания.
  const pinned = useRef(false)
  const hover = useRef<ReturnType<typeof setTimeout>>(undefined)
  const panel = useId()
  const box = useRef<HTMLSpanElement>(null)

  const link = contentId === null ? null : dndSuUrl(contentId)

  // Незавершённое ожидание наведения не должно сработать после того,
  // как компонент убрали с экрана.
  useEffect(() => () => {
    clearTimeout(hover.current)
  }, [])

  useEffect(() => {
    if (!open) return
    const outside = (event: Event) => {
      const target = event.target
      if (target instanceof Node && box.current?.contains(target) === true) return
      pinned.current = false
      setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      pinned.current = false
      setOpen(false)
    }
    // `pointerdown`, а не `click`: нажатие по другому термину должно
    // закрыть этот до того, как откроется тот.
    document.addEventListener('pointerdown', outside)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('pointerdown', outside)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  // Нечего показать — нет и знака вопроса. Пустая подсказка хуже
  // отсутствующей: человек нажимает и ничего не получает.
  if (text === null && link === null) return null

  return (
    <span className="app-term-hint" ref={box}>
      <button
        type="button"
        className="app-hint-toggle"
        aria-expanded={open}
        aria-controls={panel}
        aria-label={t('terms.hint', { name })}
        onClick={() => {
          // Нажатие не переключает открытость, а закрепляет её. Разница
          // видна с мышью: наведение уже открыло подсказку, и нажатие,
          // понятое как «переключить», её бы закрыло — а человек
          // нажимает как раз чтобы она осталась.
          clearTimeout(hover.current)
          const next = !pinned.current
          pinned.current = next
          setOpen(next)
        }}
        onPointerEnter={(event) => {
          // Мышь — да, палец — нет: на тач-экране это нажатие,
          // и оно обработается кнопкой само.
          if (event.pointerType !== 'mouse') return
          clearTimeout(hover.current)
          hover.current = setTimeout(() => {
            setOpen(true)
          }, HOVER_DELAY_MS)
        }}
        onPointerLeave={(event) => {
          if (event.pointerType !== 'mouse') return
          clearTimeout(hover.current)
          if (pinned.current) return
          setOpen(false)
        }}
      >
        ?
      </button>
      {open && (
        <span className="app-term-panel" id={panel}>
          {text !== null && (
            <Description text={text} textRu={textRu} inline className="app-term-text" />
          )}
          {link !== null && (
            <span className="app-term-link">
              <a href={link} target="_blank" rel="noreferrer noopener">
                {t('terms.dndSu')}
              </a>
            </span>
          )}
        </span>
      )}
    </span>
  )
}
